import {
  GridReadyEvent,
  IServerSideDatasource,
  RowDoubleClickedEvent,
} from "ag-grid-community";
import { useNavigate } from "react-router-dom";
import {
  Dispatch,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
} from "react";
import { AgGridReact } from "ag-grid-react";
import { GraphQLClient } from "graphql-request";
import { GOODS_ADJUSTMENT_VIEW } from "../../../../services/AgGrid/InventoryAggrid";
import { createGraphQLClientWithMiddleware } from "../../../../services/graphqlClient";
import { dateFilterModel } from "../../../../utils/Formatter/AgGridFilter";
import AgGrid from "../../../UI/AgGrid";
import { IGoodsAdjustment } from "../../../../types/Inventory/GoodsAdjustment";
import { useGoodsAdjustmentColumnDefs } from "./columnDef";
import { branchInfoFromSessionStorage } from "../../../../utils/Global";

interface Props {
  gridRef: RefObject<AgGridReact<IGoodsAdjustment>>;
  isFilter?: string | null;
  setRowCount?: Dispatch<SetStateAction<number>>;
}

const GoodsAdjustmentTable = ({ gridRef, isFilter, setRowCount }: Props) => {
  const navigate = useNavigate();
  const columnDef = useGoodsAdjustmentColumnDefs();
  const graphQLClientWithHeaderItem: GraphQLClient =
    createGraphQLClientWithMiddleware("wms");

  const datasource: IServerSideDatasource = {
    async getRows(params) {
      const { request } = params;
      const { startRow, endRow, filterModel, sortModel } = request;
      const { created_date, posted_date } = filterModel;
      const formatFilter = {
        ...filterModel,
        created_date: dateFilterModel(created_date),
        posted_date: dateFilterModel(posted_date),
      };

      try {
        const { GoodsAdjustView } = await graphQLClientWithHeaderItem.request(
          GOODS_ADJUSTMENT_VIEW,
          {
            aggridInput: {
              startRow,
              endRow,
              filterModel: formatFilter,
              sortModel,
            },
          }
        );

        if (setRowCount) {
          setRowCount(GoodsAdjustView.count as number);
        }

        params.success({
          rowData: GoodsAdjustView.results as any[],
          rowCount: GoodsAdjustView.count as number,
        });
      } catch (err) {
        console.error("err", err);

        params.fail();
      }
    },
  };

  const onFilterChanged = useCallback(
    (params: any) => {
      const instance = params.api.getFilterInstance("aggrid_status");

      switch (isFilter) {
        case "draft":
          instance?.setModel({ values: ["draft"] });
          break;
        case "wait_approve":
          instance?.setModel({ values: ["wait_approve"] });
          break;
        case "not_approved":
          instance?.setModel({ values: ["not_approved"] });
          break;
        case "finished":
          instance?.setModel({ values: ["finished"] });
          break;
        case "cancelled":
          instance?.setModel({ values: ["cancelled"] });
          break;
        default:
          instance?.setModel({
            values: ["draft", "wait_approve", "not_approved", "finished"],
          });
          break;
      }
      params.api.onFilterChanged();
    },
    [isFilter]
  );

  const onGridReady = (params: GridReadyEvent) => {
    const { branch_id, is_hq } = branchInfoFromSessionStorage();

    const instanceฺฺBranchId = params.api.getFilterInstance("branch_id");
    instanceฺฺBranchId?.setModel({
      filterType: "number",
      type: "equals",
      filter: is_hq === "false" && branch_id ? parseInt(branch_id) : undefined,
    });

    onFilterChanged(params);
    params.api.setServerSideDatasource(datasource);
  };

  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      onFilterChanged(gridRef.current);
    }
  }, [gridRef, onFilterChanged]);

  const onRowDoubleClicked = (params: RowDoubleClickedEvent) => {
    navigate(
      `/inventory/goods-adjustment/${encodeURIComponent(params.data.id)}`
    );
  };

  return (
    <AgGrid
      ref={gridRef}
      columnDefs={columnDef}
      height={665}
      onRowDoubleClicked={onRowDoubleClicked}
      onGridReady={onGridReady}
      path={"/inventory/goods_adjustment"}
    />
  );
};

export default GoodsAdjustmentTable;
